import React from 'react';
import {
    Container,
    Header,
    SpaceBetween,
    Box,
    Badge,
    ProgressBar,
    StatusIndicator,
} from '@cloudscape-design/components';
import LoadingBar from '@cloudscape-design/chat-components/loading-bar';

// Ordered pipeline stages
const STAGE_ORDER = ['fetch', 'draft', 'critic', 'revise', 'judge', 'done', 'failed'];

const STAGE_LABELS = {
    fetch:  'Fetch',
    draft:  'Draft',
    critic: 'Critic',
    revise: 'Revise',
    judge:  'Judge',
    done:   'Done',
    failed: 'Failed',
};

const STAGE_ICONS = {
    fetch:  '🌐',
    draft:  '✍️',
    critic: '🔍',
    revise: '🔧',
    judge:  '⚖️',
    done:   '✅',
    failed: '❌',
};

function stageIndex(stage) {
    const idx = STAGE_ORDER.indexOf(stage);
    return idx === -1 ? 0 : idx;
}

function stageProgress(stage) {
    // done = 100, failed = 100, others proportional
    if (stage === 'done' || stage === 'failed') return 100;
    return Math.round((stageIndex(stage) / (STAGE_ORDER.length - 2)) * 100);
}

const StageNode = ({ stage, currentStage }) => {
    const current = stageIndex(currentStage);
    const mine    = stageIndex(stage);

    let status;
    if (stage === 'failed' && currentStage === 'failed') {
        status = 'error';
    } else if (mine < current) {
        status = 'success';
    } else if (mine === current) {
        status = 'in-progress';
    } else {
        status = 'pending';
    }

    return (
        <div className={`sg-stage-node sg-stage-${status}`}>
            <span className="sg-stage-icon">{STAGE_ICONS[stage]}</span>
            <span className="sg-stage-label">{STAGE_LABELS[stage]}</span>
        </div>
    );
};

const JobStatus = ({ jobInfo, isActive }) => {
    const { stage, iterations, arc_count, title } = jobInfo;
    const isDone   = stage === 'done';
    const isFailed = stage === 'failed';

    return (
        <div className={`sg-job-status ${isActive ? 'sg-job-active' : ''}`}>
            <SpaceBetween direction="vertical" size="xs">
                {/* Title + badges */}
                <SpaceBetween direction="horizontal" size="xs">
                    <Box fontWeight="bold" fontSize="body-m">
                        {title}
                    </Box>
                    {isDone && <Badge color="green">Done</Badge>}
                    {isFailed && <Badge color="red">Failed</Badge>}
                    {isActive && !isDone && !isFailed && (
                        <Badge color="blue">Processing</Badge>
                    )}
                    {iterations > 0 && (
                        <Box fontSize="body-s" color="text-body-secondary">
                            {iterations} iter{iterations !== 1 ? 's' : ''}
                            {arc_count > 0 ? ` · ${arc_count} revision${arc_count !== 1 ? 's' : ''}` : ''}
                        </Box>
                    )}
                </SpaceBetween>

                {/* Stage pipeline visualization */}
                <div className="sg-stage-pipeline">
                    {['fetch', 'draft', 'critic', 'revise', 'judge', 'done'].map((s, i) => (
                        <React.Fragment key={s}>
                            <StageNode stage={s} currentStage={stage} />
                            {i < 5 && <div className="sg-stage-arrow">→</div>}
                        </React.Fragment>
                    ))}
                </div>

                {/* Progress bar */}
                <ProgressBar
                    value={stageProgress(stage)}
                    status={isFailed ? 'error' : isDone ? 'success' : 'in-progress'}
                    label=""
                />

                {/* Loading bar while active */}
                {isActive && !isDone && !isFailed && (
                    <LoadingBar variant="gen-ai" />
                )}
            </SpaceBetween>
        </div>
    );
};

export default function PipelineStatus({
    pipelineStages,
    streamingDocs,
    isProcessing,
}) {
    const jobIds = Object.keys(pipelineStages);
    if (!jobIds.length) return null;

    // A job is "active" if it has a streaming buffer or its stage isn't terminal
    const isActive = (jobId) => {
        const stage = pipelineStages[jobId]?.stage;
        return isProcessing && stage !== 'done' && stage !== 'failed';
    };

    return (
        <Container
            header={
                <Header variant="h2">
                    Pipeline Progress{jobIds.length > 1 ? ` (${jobIds.length} parallel)` : ''}
                </Header>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                {jobIds.map(jobId => (
                    <JobStatus
                        key={jobId}
                        jobInfo={pipelineStages[jobId]}
                        isActive={isActive(jobId)}
                    />
                ))}

                {/* Live streaming previews for each active job */}
                {Object.entries(streamingDocs || {}).map(([jobId, text]) =>
                    text ? (
                        <div key={jobId} className="sg-streaming-preview">
                            <Box variant="awsui-key-label" padding={{ bottom: 'xs' }}>
                                Live preview — {pipelineStages[jobId]?.title || jobId}
                            </Box>
                            <pre className="sg-streaming-pre">
                                {text.slice(-800)}
                            </pre>
                        </div>
                    ) : null
                )}
            </SpaceBetween>
        </Container>
    );
}
